import { useEffect, useState, useRef } from "react";
import api, { BACKEND_URL, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Plus, Trash2, Upload, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function ConsultantSettings() {
  const { consultant, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const logoRef = useRef();
  const pwaIconRef = useRef();

  useEffect(() => {
    if (consultant) {
      setForm({
        studio_name: consultant.studio_name || "",
        description: consultant.description || "",
        phone: consultant.phone || "",
        notification_email: consultant.notification_email || "",
        brand_color_primary: consultant.brand_color_primary || "#F77F2A",
        brand_color_secondary: consultant.brand_color_secondary || "#2456C6",
        retention_days: consultant.retention_days || 90,
        logo_url: consultant.logo_url,
        pwa_icon_url: consultant.pwa_icon_url,
        quick_calls: consultant.quick_calls || [],
      });
    }
  }, [consultant]);

  if (!form || !consultant) {
    return <DashboardLayout><div className="p-8"><Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" /></div></DashboardLayout>;
  }

  const u = (k, v) => setForm({ ...form, [k]: v });

  const addQC = () => {
    if (form.quick_calls.length >= 4) {
      toast.error("Max 4 tasti rapidi personalizzati");
      return;
    }
    u("quick_calls", [...form.quick_calls, { label: "", number: "", icon: "phone" }]);
  };

  const updateQC = (idx, k, v) => {
    const arr = [...form.quick_calls];
    arr[idx][k] = v;
    u("quick_calls", arr);
  };

  const removeQC = (idx) => {
    const arr = [...form.quick_calls];
    arr.splice(idx, 1);
    u("quick_calls", arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/consultant/me", form);
      await refresh();
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/consultant/me/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      u("logo_url", data.logo_url);
      await refresh();
      toast.success("Logo caricato");
    } catch (e) {
      toast.error("Errore upload logo");
    }
  };

  const uploadPwaIcon = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/consultant/me/pwa-icon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      u("pwa_icon_url", data.pwa_icon_url);
      await refresh();
      toast.success("Icona PWA caricata");
    } catch (e) {
      toast.error("Errore upload icona PWA");
    }
  };

  const publicUrl = `${window.location.origin}/c/${consultant.slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = async () => {
    try {
      const token = localStorage.getItem("gs_token");
      const res = await fetch(`${BACKEND_URL}/api/consultant/me/qr`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gostreet-qr-${consultant.slug}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download QR");
    }
  };

  return (
    <DashboardLayout role="consultant">
      <div className="p-4 sm:p-8 max-w-5xl">
        <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-1">Impostazioni studio</h1>
        <p className="text-sm text-slate-500 mb-6">Personalizza il tuo profilo pubblico e i tuoi servizi.</p>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Profile */}
          <div className="card-gs lg:col-span-2">
            <h2 className="font-bold text-[#1E293B] mb-4">Profilo pubblico</h2>
            <div className="space-y-4">
              <div>
                <label className="label-gs">Nome studio</label>
                <input data-testid="settings-studio-name" type="text" value={form.studio_name}
                  onChange={(e) => u("studio_name", e.target.value)} className="input-gs" />
              </div>
              <div>
                <label className="label-gs">Descrizione (mostrata al cliente)</label>
                <textarea data-testid="settings-description" value={form.description}
                  onChange={(e) => u("description", e.target.value)} className="input-gs resize-none" rows={2}
                  placeholder="Es: Consulente infortunistico stradale dal 2010" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-gs">Telefono</label>
                  <input data-testid="settings-phone" type="tel" value={form.phone}
                    onChange={(e) => u("phone", e.target.value)} className="input-gs" placeholder="+39 …" />
                </div>
                <div>
                  <label className="label-gs">Email notifiche</label>
                  <input data-testid="settings-email" type="email" value={form.notification_email}
                    onChange={(e) => u("notification_email", e.target.value)} className="input-gs" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-gs">Logo studio <span className="text-xs font-normal text-slate-500">(rettangolare 3:2 — usato in alto sulla pagina cliente)</span></label>
                  <div className="flex items-center gap-4 mt-2">
                    {form.logo_url ? (
                      <div className="rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden"
                        style={{ width: 120, height: 80 }}>
                        <img src={fileUrl(form.logo_url)} alt="logo" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs"
                        style={{ width: 120, height: 80 }}>No logo</div>
                    )}
                    <button onClick={() => logoRef.current?.click()} data-testid="upload-logo-button"
                      className="btn-gs-outline !py-2 !px-4 text-sm">
                      <Upload className="w-4 h-4" /> Carica
                    </button>
                    <input ref={logoRef} type="file" accept="image/*" hidden
                      onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Consigliato: 720×480 PNG/JPG, max 3 MB</p>
                </div>

                <div>
                  <label className="label-gs">Icona PWA <span className="text-xs font-normal text-slate-500">(quadrata — icona sul telefono del cliente)</span></label>
                  <div className="flex items-center gap-4 mt-2">
                    {form.pwa_icon_url ? (
                      <div className="rounded-2xl border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden"
                        style={{ width: 80, height: 80 }}>
                        <img src={fileUrl(form.pwa_icon_url)} alt="pwa icon" className="w-full h-full object-cover rounded-xl" />
                      </div>
                    ) : (
                      <div className="rounded-2xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs"
                        style={{ width: 80, height: 80 }}>No icon</div>
                    )}
                    <button onClick={() => pwaIconRef.current?.click()} data-testid="upload-pwa-icon-button"
                      className="btn-gs-outline !py-2 !px-4 text-sm">
                      <Upload className="w-4 h-4" /> Carica
                    </button>
                    <input ref={pwaIconRef} type="file" accept="image/*" hidden
                      onChange={(e) => e.target.files[0] && uploadPwaIcon(e.target.files[0])} />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Consigliato: 512×512 PNG quadrato, max 3 MB</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="label-gs">Colore primario</label>
                  <input type="color" value={form.brand_color_primary}
                    onChange={(e) => u("brand_color_primary", e.target.value)}
                    className="w-full h-12 rounded-xl border-2 border-slate-200 cursor-pointer" />
                </div>
                <div>
                  <label className="label-gs">Colore secondario</label>
                  <input type="color" value={form.brand_color_secondary}
                    onChange={(e) => u("brand_color_secondary", e.target.value)}
                    className="w-full h-12 rounded-xl border-2 border-slate-200 cursor-pointer" />
                </div>
                <div>
                  <label className="label-gs">Ritenzione (giorni)</label>
                  <select data-testid="settings-retention" value={form.retention_days}
                    onChange={(e) => u("retention_days", parseInt(e.target.value))} className="input-gs">
                    <option value={30}>30 giorni</option>
                    <option value={60}>60 giorni</option>
                    <option value={90}>90 giorni</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* QR */}
          <div className="card-gs">
            <h2 className="font-bold text-[#1E293B] mb-4">Il tuo QR Code</h2>
            <div className="bg-white p-4 rounded-2xl border-2 border-slate-200 flex items-center justify-center mb-3">
              <QRCodeSVG value={publicUrl} size={180} fgColor="#1E293B" />
            </div>
            <div className="text-xs text-slate-500 break-all mb-3 px-2 font-mono">{publicUrl}</div>
            <div className="space-y-2">
              <button onClick={copyLink} data-testid="copy-link-button" className="btn-gs-outline w-full !py-2 text-sm">
                {copied ? <><Check className="w-4 h-4" /> Copiato!</> : <><Copy className="w-4 h-4" /> Copia link</>}
              </button>
              <button onClick={downloadQr} data-testid="download-qr-button" className="btn-gs-secondary w-full !py-2 text-sm">
                <Download className="w-4 h-4" /> Scarica QR (PNG)
              </button>
            </div>
          </div>

          {/* Quick calls */}
          <div className="card-gs lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-[#1E293B]">Tasti chiamata rapida</h2>
                <p className="text-xs text-slate-500">Numeri convenzionati che appariranno al cliente (max 4)</p>
              </div>
              <button onClick={addQC} data-testid="add-quick-call-button" className="btn-gs-outline !py-2 !px-3 text-sm">
                <Plus className="w-4 h-4" /> Aggiungi
              </button>
            </div>
            {form.quick_calls.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center border-2 border-dashed border-slate-200 rounded-xl">
                Nessun tasto configurato. I clienti vedranno solo "112" e "Consulente".
              </div>
            ) : (
              <div className="space-y-2">
                {form.quick_calls.map((qc, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`qc-label-${idx}`} type="text" value={qc.label}
                      onChange={(e) => updateQC(idx, "label", e.target.value)}
                      placeholder="Es: Carro Attrezzi"
                      className="input-gs col-span-5" />
                    <input data-testid={`qc-number-${idx}`} type="tel" value={qc.number}
                      onChange={(e) => updateQC(idx, "number", e.target.value)}
                      placeholder="+39 …"
                      className="input-gs col-span-6" />
                    <button onClick={() => removeQC(idx)} data-testid={`qc-remove-${idx}`}
                      className="col-span-1 text-red-600 hover:bg-red-50 rounded-lg p-3 flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 sticky bottom-4">
          <button onClick={save} disabled={saving} data-testid="save-settings-button"
            className="btn-gs-primary w-full sm:w-auto disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva impostazioni"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
